export const UPGRADE_IDS = ['wateringCan', 'sprinkler', 'compost', 'beehive', 'boots'] as const;

export type UpgradeId = (typeof UPGRADE_IDS)[number];

export interface UpgradeDef {
  readonly id: UpgradeId;
  readonly name: string;
  readonly emoji: string;
  /** Cost to go from level `i` to `i + 1`. Its length is the max level. */
  readonly costs: readonly number[];
  readonly unlockLevel: number;
  readonly describe: (level: number) => string;
}

export const UPGRADES: Readonly<Record<UpgradeId, UpgradeDef>> = {
  wateringCan: {
    id: 'wateringCan',
    name: 'Big Watering Can',
    emoji: '🚿',
    costs: [80, 450],
    unlockLevel: 2,
    describe: (level) =>
      ['Waters a single plot.', 'Waters a plus-shaped patch of 5 plots.', 'Waters a 3×3 patch.'][
        level
      ] ?? '',
  },
  sprinkler: {
    id: 'sprinkler',
    name: 'Sprinklers',
    emoji: '💦',
    costs: [180, 700, 2200],
    unlockLevel: 3,
    describe: (level) =>
      level === 0
        ? 'Automatically waters every plot on a timer.'
        : `Waters every plot every ${sprinklerInterval(level)}s.`,
  },
  compost: {
    id: 'compost',
    name: 'Compost Bin',
    emoji: '🪱',
    costs: [120, 500, 1600],
    unlockLevel: 2,
    describe: (level) =>
      level === 0
        ? 'Rich soil makes flowers grow faster.'
        : `Flowers grow ${Math.round((growthMultiplier(level) - 1) * 100)}% faster.`,
  },
  beehive: {
    id: 'beehive',
    name: 'Beehive',
    emoji: '🐝',
    costs: [150, 600, 2000],
    unlockLevel: 3,
    describe: (level) =>
      `Cross-pollination ${Math.round(crossPollinationChance(level) * 100)}% · golden blooms ${Math.round(goldenChance(level) * 100)}%.`,
  },
  boots: {
    id: 'boots',
    name: 'Comfy Boots',
    emoji: '🥾',
    costs: [60, 300],
    unlockLevel: 1,
    describe: (level) =>
      level === 0 ? 'Walk a little faster around the farm.' : `Walk ${level * 25}% faster.`,
  },
};

export function maxLevel(id: UpgradeId): number {
  return UPGRADES[id].costs.length;
}

export function upgradeCost(id: UpgradeId, currentLevel: number): number | null {
  return UPGRADES[id].costs[currentLevel] ?? null;
}

export function isUpgradeId(value: unknown): value is UpgradeId {
  return typeof value === 'string' && (UPGRADE_IDS as readonly string[]).includes(value);
}

/** Grid offsets (dCol, dRow) watered by one use of the watering can. */
export function wateringPattern(level: number): ReadonlyArray<readonly [number, number]> {
  if (level <= 0) return [[0, 0]];
  if (level === 1) {
    return [
      [0, 0],
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ];
  }
  const all: Array<[number, number]> = [];
  for (let dr = -1; dr <= 1; dr++) for (let dc = -1; dc <= 1; dc++) all.push([dc, dr]);
  return all;
}

export function sprinklerInterval(level: number): number {
  return [Infinity, 90, 50, 25][level] ?? 25;
}

export function growthMultiplier(level: number): number {
  return 1 + 0.2 * level;
}

export function crossPollinationChance(level: number): number {
  return 0.2 + 0.15 * level;
}

export function goldenChance(level: number): number {
  return 0.03 + 0.02 * level;
}

export function moveSpeedMultiplier(level: number): number {
  return 1 + 0.25 * level;
}
